// Header.js
import React, { useState } from "react";
import RightSideHeader from "./rightSideHeader";
import logo from "../../img/MCL svg.svg";
import logonkc from "../../img/nkc.png";
import logoDlb from "../../img/trentlogo.png";
import config from "../../config/config";
import { useTheme } from "../../context/themeContext";
import {
  Brightness4,
  Brightness7,
  Palette as PaletteIcon,
} from "@material-ui/icons";
import {
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Modal,
  Box,
  Button,
} from "@material-ui/core";
import ThemeCustomizationModal from "./ThemeCustomizationModal";
import CommanModal from "../CommonComponents/CommanModal/CommanModal";
import CreateTickets from "./CreateTickets";

const Header = ({ events, setEvents }) => {
  const { theme, toggleTheme, setIsCustomizing, applyPreset, themePresets } =
    useTheme();
  const [themeMenuAnchor, setThemeMenuAnchor] = useState(null);
  const [customizationModalOpen, setCustomizationModalOpen] = useState(false);
  const [isOpen, setOpen] = useState(false);
  const handleThemeMenuClick = (event) => {
    setThemeMenuAnchor(event.currentTarget);
  };

  const handleThemeMenuClose = () => {
    setThemeMenuAnchor(null);
  };
  const handleQuickToggle = () => {
    toggleTheme();
    handleThemeMenuClose();
  };

  const handlePresetSelect = (presetKey) => {
    applyPreset(presetKey);
    handleThemeMenuClose();
  };

  const handleOpenCustomization = () => {
    setCustomizationModalOpen(true);
    setIsCustomizing(true);
    handleThemeMenuClose();
  };

  const handleCloseCustomization = () => {
    setCustomizationModalOpen(false);
    setIsCustomizing(false);
  };

  // Simplified styles
  const headerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 16px",
    height: "70px", // match original
    background:
      theme.headerGradient || `linear-gradient(90deg, #fff 10%, #003a6b 90%)`,
    boxShadow: `3px -3px 6px 3px ${theme.shadowColor || "#d1d1d1"}`,
    position: "fixed",
    left: 0,
    top: 0,
    width: "100%",
    zIndex: 1299,
  };

  const logoStyle = {
    display: "flex",
    alignItems: "center",
  };

  const logoImgStyle = {
    height: "40px",
    width: "auto",
  };

  const rightSectionStyle = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };

  const themeButtonStyle = {
    backgroundColor: theme.cardBackground,
    border: `1px solid ${theme.borderColor}`,
    borderRadius: "8px",
    color: theme.text,
  };

  const menuItemStyle = {
    color: theme.text,
  };

  const raiseAnIssueHandler = () => {
    setOpen(true);
  };

  return (
    <>
      <header style={{ position: "relative", zIndex: 1000 }}>
        <div style={headerStyle}>
          {/* Logo Section */}
          <div style={logoStyle}>
            {config.NkcWeb && (
              <img src={logonkc} alt="NKC Logo" style={logoImgStyle} />
            )}
            {config.VTSWeb && (
              <img src={logo} alt="MCL Logo" style={logoImgStyle} />
            )}
            {config.BJBWeb && (
              <img src={logoDlb} alt="Trent Logo" style={logoImgStyle} />
            )}
          </div>

          {/* Right Section */}
          <div style={rightSectionStyle}>
            <RightSideHeader
              events={events}
              setEvents={setEvents}
              raiseAnIssueHandler={raiseAnIssueHandler}
              isOpen={isOpen}
              setOpen={setOpen}
            />
          </div>
        </div>

        <Modal
          open={Boolean(themeMenuAnchor)}
          onClose={handleThemeMenuClose}
          aria-labelledby="theme-modal-title"
          aria-describedby="theme-modal-description"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 320,
              bgcolor: "background.paper",
              border: "2px solid",
              borderColor: "divider",
              borderRadius: 2,
              boxShadow: 24,
              p: 0,
              maxHeight: "80vh",
              overflow: "auto",
            }}
          >
            {/* Header */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
              <Typography id="theme-modal-title" variant="h6" component="h2">
                Theme Settings
              </Typography>
            </Box>

            {/* Content */}
            <Box sx={{ p: 1 }}>
              {/* Quick Toggle */}
              <MenuItem onClick={handleQuickToggle} style={menuItemStyle}>
                <ListItemIcon>
                  {theme.mode === "dark" ? <Brightness7 /> : <Brightness4 />}
                </ListItemIcon>
                <ListItemText
                  primary={`Switch to ${
                    theme.mode === "dark" ? "Light" : "Dark"
                  } Mode`}
                  style={{ color: "black" }}
                />
              </MenuItem>

              <Divider sx={{ my: 1 }} />

              {/* Theme Presets */}
              <Typography
                variant="caption"
                sx={{ px: 2, py: 1, color: "text.secondary", display: "block" }}
              >
                Themes
              </Typography>

              {Object.entries(themePresets).map(([key, preset]) => (
                <MenuItem
                  key={key}
                  onClick={() => handlePresetSelect(key)}
                  style={{
                    ...menuItemStyle,
                    backgroundColor:
                      theme.name === preset.name
                        ? `${theme.primary}20`
                        : "transparent",
                  }}
                >
                  <ListItemIcon>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        backgroundColor: preset.primary,
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={preset.name}
                    style={{ color: "black" }}
                  />
                </MenuItem>
              ))}

              <Divider sx={{ my: 1 }} />

              {/* Customization Option */}
              <MenuItem onClick={handleOpenCustomization} style={menuItemStyle}>
                <ListItemIcon>
                  <PaletteIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Customize Theme"
                  style={{ color: "black" }}
                />
              </MenuItem>
            </Box>

            {/* Footer with Close Button (Optional) */}
            <Box
              sx={{
                p: 2,
                borderTop: 1,
                borderColor: "divider",
                textAlign: "right",
              }}
            >
              <Button
                onClick={handleThemeMenuClose}
                variant="outlined"
                size="small"
              >
                Close
              </Button>
            </Box>
          </Box>
        </Modal>
        {isOpen && (
          <CommanModal
            open={isOpen}
            setOpen={setOpen}
            handleSubmit={""}
            title={"Raise An Issue"}
            remarStatus={false}
          >
            <CreateTickets />
          </CommanModal>
        )}
      </header>

      <ThemeCustomizationModal
        open={customizationModalOpen}
        onClose={handleCloseCustomization}
      />
    </>
  );
};

export default Header;
